import React, { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Scissors, ChevronLeft, ChevronRight, Copy, X, FileText } from "lucide-react";

const DOW_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function CalendarView({ mode, rangeAnchor, setRangeAnchor, data, onOpenDay, onCloneToDate, onApplyTemplate, onClearException }) {
  const byDate = useMemo(() => {
    const m = new Map();
    (data.results || []).forEach((r) => m.set(r.date, r));
    return m;
  }, [data]);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const todayIso = today.toISOString().slice(0, 10);

  const step = (delta) => {
    const next = new Date(rangeAnchor);
    if (mode === "month") {
      // Snap to day 1 before stepping so setMonth doesn't roll over
      // (Jan 31 + 1 month would otherwise land on Mar 3 and skip Feb).
      next.setDate(1);
      next.setMonth(next.getMonth() + delta);
    } else {
      next.setDate(next.getDate() + delta * 7 * 13);
    }
    setRangeAnchor(next);
  };
  const goToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); setRangeAnchor(d); };

  const title = useMemo(() => {
    if (mode === "month") {
      return rangeAnchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
    }
    // Quarter: show range
    const start = data.start ? new Date(data.start) : rangeAnchor;
    const end = new Date(start); end.setDate(end.getDate() + 90);
    const opt = { month: "short", day: "numeric", year: "numeric" };
    return `${start.toLocaleDateString(undefined, opt)} → ${end.toLocaleDateString(undefined, opt)}`;
  }, [mode, rangeAnchor, data.start]);

  return (
    <div className="panel mb-3">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="rounded-sm h-7 w-7 p-0" onClick={() => step(-1)} data-testid="cal-prev">
            <ChevronLeft className="w-3 h-3" />
          </Button>
          <Button variant="outline" size="sm" className="rounded-sm h-7 text-xs px-2" onClick={goToday} data-testid="cal-today">Today</Button>
          <Button variant="outline" size="sm" className="rounded-sm h-7 w-7 p-0" onClick={() => step(1)} data-testid="cal-next">
            <ChevronRight className="w-3 h-3" />
          </Button>
          <div className="font-display font-bold text-sm ml-2" data-testid="cal-title">{title}</div>
          {data.loading && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground ml-2" />}
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
          {mode === "month" ? "click a day to open its Week · fuchsia = exception" : "13 weeks · click any day → Week editor"}
        </div>
      </div>

      {mode === "month" ? (
        <MonthGrid byDate={byDate} results={data.results || []} todayIso={todayIso}
          onOpenDay={onOpenDay} onCloneToDate={onCloneToDate} onApplyTemplate={onApplyTemplate} onClearException={onClearException} />
      ) : (
        <QuarterGrid byDate={byDate} results={data.results || []} todayIso={todayIso}
          onOpenDay={onOpenDay} onCloneToDate={onCloneToDate} onApplyTemplate={onApplyTemplate} onClearException={onClearException} />
      )}
    </div>
  );
}

export function DayCell({ day, onOpenDay, onCloneToDate, onApplyTemplate, onClearException, todayIso, compact }) {
  if (!day) return <div className="border-r border-b border-border/50" />;
  const isToday = day.date === todayIso;
  const isExc = day.is_exception;
  const coveredPct = Math.min(100, (day.covered_sec / (24 * 3600)) * 100);
  const label = new Date(day.date).getDate();
  const height = compact ? "h-10" : "h-24";
  return (
    <div
      className={`relative border-r border-b border-border/50 ${height} group cursor-pointer overflow-hidden ${
        isExc ? "bg-fuchsia-500/10" : "hover:bg-muted/30"
      } ${isToday ? "ring-1 ring-inset ring-fuchsia-400" : ""}`}
      onClick={() => onOpenDay(day.date)}
      data-testid={`cal-day-${day.date}`}
      title={
        `${day.date}${isExc ? " · EXCEPTION" : ""}\n` +
        `${day.item_count} items · ${(day.covered_sec / 3600).toFixed(1)}h covered` +
        (day.exception_reason ? `\nReason: ${day.exception_reason}` : "")
      }
    >
      <div className="absolute inset-x-1 top-1 flex items-center justify-between text-[10px]">
        <span className={`font-mono ${isToday ? "text-fuchsia-400 font-bold" : "text-muted-foreground"}`}>
          {label}
        </span>
        {isExc && <Scissors className="w-2.5 h-2.5 text-fuchsia-400" />}
      </div>
      {!compact && (
        <>
          <div className="absolute inset-x-1 top-6 text-[10px] font-mono text-muted-foreground">
            {day.item_count > 0 ? `${day.item_count} slots` : "empty"}
          </div>
          <div className="absolute inset-x-1 top-11 text-[9px] font-mono text-muted-foreground">
            {day.covered_sec > 0 && `${(day.covered_sec / 3600).toFixed(1)}h`}
          </div>
          {isExc && day.exception_reason && (
            <div className="absolute inset-x-1 bottom-4 text-[9px] font-mono text-fuchsia-500 truncate">
              {day.exception_reason}
            </div>
          )}
        </>
      )}
      {/* Coverage bar at bottom */}
      <div className="absolute inset-x-0 bottom-0 h-1 bg-muted/30">
        <div className={`h-full ${isExc ? "bg-fuchsia-500" : "bg-emerald-500/70"}`} style={{ width: `${coveredPct}%` }} />
      </div>
      {/* Hover actions */}
      <div className="absolute inset-x-0 bottom-1.5 hidden group-hover:flex items-center justify-center gap-1 z-10">
        {!isExc && (
          <button
            className="text-[9px] px-1.5 py-0.5 rounded-sm bg-fuchsia-500 text-white hover:bg-fuchsia-600"
            onClick={(e) => { e.stopPropagation(); onCloneToDate(day.date); }}
            data-testid={`cal-clone-${day.date}`}
            title="Create a date-specific exception starting from this day's weekly template"
          >
            <Copy className="w-2.5 h-2.5 inline mr-0.5" /> exc
          </button>
        )}
        {onApplyTemplate && (
          <button
            className="text-[9px] px-1.5 py-0.5 rounded-sm bg-cyan-500 text-white hover:bg-cyan-600"
            onClick={(e) => { e.stopPropagation(); onApplyTemplate(day.date); }}
            data-testid={`cal-tpl-${day.date}`}
            title="Apply or save a schedule template for this date"
          >
            <FileText className="w-2.5 h-2.5 inline mr-0.5" /> tpl
          </button>
        )}
        {isExc && (
          <button
            className="text-[9px] px-1.5 py-0.5 rounded-sm bg-rose-500 text-white hover:bg-rose-600"
            onClick={(e) => { e.stopPropagation(); onClearException(day.date); }}
            data-testid={`cal-revert-${day.date}`}
            title="Revert to weekly template"
          >
            <X className="w-2.5 h-2.5 inline mr-0.5" /> revert
          </button>
        )}
      </div>
    </div>
  );
}

function MonthGrid({ results, todayIso, onOpenDay, onCloneToDate, onApplyTemplate, onClearException }) {
  // The backend returned 42 sequential days starting from the Monday of the
  // visible month's first week (see the useEffect). Split into 6 rows of 7.
  const rows = [];
  for (let i = 0; i < 6; i++) rows.push(results.slice(i * 7, (i + 1) * 7));
  return (
    <div>
      <div className="grid grid-cols-7">
        {DOW_LABELS.map((d) => (
          <div key={d} className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground text-center py-2 border-b border-border">
            {d}
          </div>
        ))}
      </div>
      {rows.map((row, i) => (
        <div key={i} className="grid grid-cols-7">
          {row.map((day, j) => (
            <DayCell
              key={day?.date || `${i}-${j}`}
              day={day}
              todayIso={todayIso}
              onOpenDay={onOpenDay}
              onCloneToDate={onCloneToDate}
              onApplyTemplate={onApplyTemplate}
              onClearException={onClearException}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

function QuarterGrid({ results, todayIso, onOpenDay, onCloneToDate, onApplyTemplate, onClearException }) {
  // 91 days = 13 rows × 7 cols. Compact cells.
  const rows = [];
  for (let i = 0; i < 13; i++) rows.push(results.slice(i * 7, (i + 1) * 7));
  return (
    <div>
      <div className="grid grid-cols-[80px_repeat(7,1fr)]">
        <div className="border-b border-border" />
        {DOW_LABELS.map((d) => (
          <div key={d} className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground text-center py-2 border-b border-border">
            {d}
          </div>
        ))}
      </div>
      {rows.map((row, i) => {
        const first = row.find(Boolean);
        const wkLabel = first ? new Date(first.date).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
        return (
          <div key={i} className="grid grid-cols-[80px_repeat(7,1fr)]">
            <div className="text-[10px] font-mono text-muted-foreground px-2 py-2 border-b border-r border-border/50">
              wk {i + 1}
              <div className="text-[9px] opacity-70">{wkLabel}</div>
            </div>
            {row.map((day, j) => (
              <DayCell
                key={day?.date || `${i}-${j}`}
                day={day}
                todayIso={todayIso}
                onOpenDay={onOpenDay}
                onCloneToDate={onCloneToDate}
                onApplyTemplate={onApplyTemplate}
                onClearException={onClearException}
                compact
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}
